import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

export type ToastType = 'error' | 'success' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

const TYPE_STYLES: Record<ToastType, string> = {
  success: 'bg-teal-900/80 border-teal-500/40 text-teal-100',
  error: 'bg-red-900/80 border-red-500/40 text-red-100',
  info: 'bg-indigo-900/80 border-indigo-500/40 text-indigo-100',
};

export const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 24, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 16, scale: 0.95 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border backdrop-blur-md shadow-xl text-sm font-medium pointer-events-auto ${TYPE_STYLES[toast.type]}`}
      role='alert'
      aria-live='assertive'
    >
      <span className='flex-1'>{toast.message}</span>
      {toast.action && (
        <button
          type='button'
          onClick={() => {
            toast.action!.onClick();
            onDismiss(toast.id);
          }}
          className='shrink-0 text-xs font-bold underline underline-offset-2 opacity-90 hover:opacity-100 transition-opacity'
        >
          {toast.action.label}
        </button>
      )}
      <button
        type='button'
        onClick={() => onDismiss(toast.id)}
        className='shrink-0 p-1 -m-1 rounded opacity-70 hover:opacity-100 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400'
        aria-label='Dismiss notification'
      >
        <X size={14} />
      </button>
    </motion.div>
  );
};

interface ToastListProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

export const ToastList: React.FC<ToastListProps> = ({ toasts, onDismiss }) => {
  return (
    <div
      className='fixed z-[9999] bottom-6 left-0 right-0 flex flex-col items-center gap-2 px-4 pointer-events-none sm:bottom-auto sm:top-6 sm:right-6 sm:left-auto sm:items-end sm:max-w-sm'
      aria-label='Notifications'
    >
      <AnimatePresence mode='popLayout'>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>
  );
};
